import { MAX_EXPIRY_WARNING_DAYS, MIN_EXPIRY_WARNING_DAYS } from '@felix/domain';
import { Alert, Button, Group, Modal, NumberInput, Skeleton, Stack } from '@mantine/core';
import {
  IconAlertCircle,
  IconBooks,
  IconCircleCheck,
  IconInfoCircle,
  IconSettings,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { Panel } from '../../Panel.js';
import { formatQuantity } from '../materials/inventory.js';
import classes from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { useProvisionError } from './ProvisionDialogs.js';
import css from './Provisions.module.css';
import type { ProvisionOffice } from './source.js';
import type {
  ProvisionLibraryInfo,
  ProvisionLibraryReport,
  ProvisionPermissions,
  ProvisionSettings,
} from './types.js';

/**
 * Ofiste kumanyanın şirket verisi (ADR 0099 §1, §9, §12): ürünün başlangıç kütüphanesi (kategori
 * bazında önizleme, önce `dryRun` raporu, sonra onayla uygulama) ve son kullanma uyarı penceresi.
 */
export function ProvisionOfficeCards({
  office,
  can,
  onChanged,
}: {
  office: ProvisionOffice;
  can: ProvisionPermissions;
  onChanged: () => void;
}) {
  return (
    <div className={css.officeRow}>
      <LibraryCard office={office} allowed={can.library === true} onChanged={onChanged} />
      <SettingsCard office={office} allowed={can.settings === true} onChanged={onChanged} />
    </div>
  );
}

function ReportLines({ report }: { report: ProvisionLibraryReport }) {
  const { t } = useTranslation();
  const l = (key: string, options: Record<string, unknown>) =>
    t(`provisions.library.${key}`, options);
  const lines = [
    report.items.created > 0 ? l('created', { count: report.items.created }) : null,
    report.items.adopted > 0 ? l('adopted', { count: report.items.adopted }) : null,
    report.items.filled > 0 ? l('filled', { count: report.items.filled }) : null,
    report.items.unchanged > 0 ? l('unchanged', { count: report.items.unchanged }) : null,
    report.items.skipped > 0 ? l('skipped', { count: report.items.skipped }) : null,
    report.categories.created > 0 ? l('categories', { count: report.categories.created }) : null,
    report.differs.items > 0 ? l('differs', { items: report.differs.items }) : null,
  ].filter((line): line is string => line !== null);
  const changes =
    report.items.created + report.items.adopted + report.items.filled + report.categories.created;
  return (
    <>
      <ul className={css.reportList}>
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {changes === 0 ? <p className={classes.muted}>{l('nothing', {})}</p> : null}
      {report.conflicts.length > 0 ? (
        <ul className={css.reportList} aria-label={t('provisions.office.conflicts')}>
          {report.conflicts.slice(0, 5).map((conflict) => (
            <li key={conflict.itemCode}>
              {l('conflict', {
                name: conflict.name,
                itemCode: conflict.itemCode,
                type: t(`materials.types.${conflict.materialType}`, {
                  defaultValue: conflict.materialType,
                }),
              })}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function LibraryCard({
  office,
  allowed,
  onChanged,
}: {
  office: ProvisionOffice;
  allowed: boolean;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.office.${key}`, options);
  const errorText = useProvisionError();
  const [info, setInfo] = useState<ProvisionLibraryInfo | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [preview, setPreview] = useState<ProvisionLibraryReport | null>(null);
  const [applied, setApplied] = useState<ProvisionLibraryReport | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setFailed(null);
    office
      .library()
      .then(setInfo)
      .catch((reason) => setFailed(errorCode(reason)));
  }, [office]);
  useEffect(load, [load]);

  const runPreview = async () => {
    setError(null);
    setBusy('preview');
    try {
      setPreview(await office.applyLibrary(true));
    } catch (reason) {
      setError(errorCode(reason));
    } finally {
      setBusy(null);
    }
  };
  const apply = async () => {
    setError(null);
    setBusy('apply');
    try {
      const report = await office.applyLibrary(false);
      setPreview(null);
      setApplied(report);
      onChanged();
    } catch (reason) {
      setError(errorCode(reason));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel
      title={t('provisions.sections.library')}
      icon={<IconBooks size={18} />}
      className={css.officeCard}
    >
      {failed ? (
        <EmptyState tone="danger" title={errorText(failed)} />
      ) : !info ? (
        <Skeleton height={120} radius="sm" />
      ) : (
        <Stack gap="xs">
          <p className={css.libraryLine}>
            <strong>{t('provisions.library.version')}:</strong> {info.version} ·{' '}
            {t('provisions.hints.library', {
              items: formatQuantity(info.items, locale),
              categories: info.mainCategories,
            })}
          </p>
          {info.categories && info.categories.length > 0 ? (
            <section
              className={css.libraryGrid}
              aria-label={k('preview')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={classes.miniTable}>
                <thead>
                  <tr>
                    <th scope="col">{t('provisions.columns.category')}</th>
                    <th scope="col" data-numeric>
                      {t('provisions.columns.subCategory')}
                    </th>
                    <th scope="col" data-numeric>
                      {k('items')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {info.categories.map((c) => (
                    <tr key={c.categoryCode}>
                      <td>
                        <span className={css.code}>{c.categoryCode}</span> {c.mainCategory}
                      </td>
                      <td data-numeric>{c.subCategories}</td>
                      <td data-numeric>{formatQuantity(c.items, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}
          <p className={classes.muted}>{t('provisions.hints.libraryUpdateShips')}</p>
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
              {errorText(error)}
            </Alert>
          ) : null}
          {applied ? (
            <Alert
              color="green"
              icon={<IconCircleCheck size={18} />}
              role="status"
              withCloseButton
              closeButtonLabel={k('close')}
              onClose={() => setApplied(null)}
            >
              <strong>{k('appliedTitle')}</strong>
              <ReportLines report={applied} />
            </Alert>
          ) : null}
          {allowed ? (
            <Group justify="flex-end">
              <Button
                variant="light"
                loading={busy === 'preview'}
                onClick={() => void runPreview()}
              >
                {t('provisions.actions.applyLibrary')}
              </Button>
            </Group>
          ) : (
            <p className={classes.muted}>{k('libraryForbidden')}</p>
          )}
        </Stack>
      )}
      <Modal
        opened={preview !== null}
        onClose={() => setPreview(null)}
        title={k('previewTitle')}
        closeButtonProps={{ 'aria-label': k('close') }}
        size="lg"
      >
        {preview ? (
          <Stack gap="sm">
            <Alert color="info" variant="light" role="note" icon={<IconInfoCircle size={18} />}>
              {k('dryRun')}
            </Alert>
            <ReportLines report={preview} />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setPreview(null)}>
                {t('provisions.actions.cancel')}
              </Button>
              <Button loading={busy === 'apply'} onClick={() => void apply()}>
                {k('confirmApply')}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Panel>
  );
}

/**
 * Settings ▸ Provisions: son kullanma uyarı penceresi (ADR 0099 §9). Ayarlar sayfasında da
 * görünür; düzenleme `settings.edit` ister (yoksa salt okunur).
 */
export function ProvisionSettingsSection({
  office,
  canEdit,
}: {
  office: ProvisionOffice;
  canEdit: boolean;
}) {
  return <SettingsCard office={office} allowed={canEdit} onChanged={() => {}} />;
}

function SettingsCard({
  office,
  allowed,
  onChanged,
}: {
  office: ProvisionOffice;
  allowed: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.office.${key}`, options);
  const errorText = useProvisionError();
  const [settings, setSettings] = useState<ProvisionSettings | null>(null);
  const [days, setDays] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    office
      .settings()
      .then((loaded) => {
        setSettings(loaded);
        setDays(loaded.expiryWarningDays);
      })
      .catch((reason) => setError(errorCode(reason)));
  }, [office]);

  const valid =
    typeof days === 'number' &&
    Number.isInteger(days) &&
    days >= MIN_EXPIRY_WARNING_DAYS &&
    days <= MAX_EXPIRY_WARNING_DAYS;
  const save = async () => {
    if (!settings || !valid) return;
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      const next = await office.saveSettings({
        rowVersion: settings.rowVersion,
        expiryWarningDays: days,
      });
      setSettings(next);
      setDays(next.expiryWarningDays);
      setSaved(true);
      onChanged();
    } catch (reason) {
      setError(errorCode(reason));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title={t('provisions.sections.settings')}
      icon={<IconSettings size={18} />}
      className={css.officeCard}
    >
      {!settings && !error ? (
        <Skeleton height={80} radius="sm" />
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Stack gap="xs">
            {error ? (
              <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
                {errorText(error)}
              </Alert>
            ) : null}
            {saved ? (
              <Alert color="green" icon={<IconCircleCheck size={18} />} role="status">
                {k('settingsSaved')}
              </Alert>
            ) : null}
            <NumberInput
              label={t('provisions.forms.expiryWarningDays')}
              description={k('warningHint')}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              min={MIN_EXPIRY_WARNING_DAYS}
              max={MAX_EXPIRY_WARNING_DAYS}
              allowDecimal={false}
              readOnly={!allowed}
              value={days}
              onChange={(value) => {
                setSaved(false);
                setDays(typeof value === 'number' ? value : '');
              }}
              error={
                days !== '' && !valid
                  ? t('provisions.errors.INVALID_PROVISION_SETTINGS')
                  : undefined
              }
              className={css.daysInput}
            />
            {allowed ? (
              <Group justify="flex-end">
                <Button
                  type="submit"
                  loading={busy}
                  disabled={!valid || days === settings?.expiryWarningDays}
                >
                  {t('provisions.actions.save')}
                </Button>
              </Group>
            ) : (
              <p className={classes.muted}>{k('settingsForbidden')}</p>
            )}
          </Stack>
        </form>
      )}
    </Panel>
  );
}
